/**
 * 文章流（栏目 03 / 04 / 05 通用）
 *
 * 数据来自 /articles/manifest.json（运行时 fetch，方案 B）
 * 加载中显示骨架；加载失败/为空显示"敬请期待"占位。
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  listArticlesByCategory,
  type ArticleCategory,
  type ArticleMeta
} from '@/data/articles'
import { track } from '@/lib/track'

export function ArticleStream({ category }: { category: ArticleCategory }) {
  const [state, setState] = useState<
    { status: 'loading' } | { status: 'ok'; items: ArticleMeta[] } | { status: 'error' }
  >({ status: 'loading' })

  useEffect(() => {
    let mounted = true
    listArticlesByCategory(category)
      .then((items) => mounted && setState({ status: 'ok', items }))
      .catch(() => mounted && setState({ status: 'error' }))
    return () => {
      mounted = false
    }
  }, [category])

  if (state.status === 'loading') {
    return (
      <div className="space-y-3">
        {[0, 1].map((i) => (
          <div key={i} className="card animate-pulse">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-xl bg-ink-100 shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-ink-100 rounded w-3/4" />
                <div className="h-3 bg-ink-100 rounded w-full" />
                <div className="h-3 bg-ink-100 rounded w-1/3" />
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (state.status === 'error' || state.items.length === 0) {
    return (
      <div className="card border border-dashed border-ink-200 bg-paper-100/50 text-center py-8">
        <div className="text-2xl mb-2">📝</div>
        <div className="text-ink-700 mb-1">本栏目正在筹备中</div>
        <div className="text-ink-500 text-sm leading-relaxed">
          关注「卓越方法星球」服务号，
          <br />
          首批内容上线时第一时间收到推送。
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {state.items.map((m) => (
        <Link
          key={m.slug}
          to={`/article/${m.slug}`}
          onClick={() => {
            track('drill_click', {
              target: 'open_article',
              from: 'article_stream',
              category,
              slug: m.slug
            })
          }}
          className="block card hover:shadow-md transition active:scale-[0.99]"
        >
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl bg-brand-50 border border-brand-100 flex items-center justify-center text-2xl shrink-0">
              {m.cover_emoji}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-ink-900 font-medium leading-snug">{m.title}</div>
              {m.subtitle && (
                <div className="text-ink-500 text-sm mt-1 leading-relaxed">{m.subtitle}</div>
              )}
              <div className="mt-2 flex items-center gap-3 text-xs text-ink-400">
                <span>
                  {m.author} · {m.read_minutes} 分钟
                </span>
                {m.free ? (
                  <span className="text-brand-700">免费阅读</span>
                ) : (
                  <span className="text-accent-700">🔒 关注后解锁</span>
                )}
              </div>
            </div>
          </div>
        </Link>
      ))}
    </div>
  )
}
